import { BoardState, Position } from '../model/BoardState';
import { CheckmateChecker } from './CheckmateChecker';
import { MoveStrategy } from './MoveStrategy';
import { CaptureMoveDetector } from './CaptureMoveDetector';
import logger from '../utils/Logger';

export class ComputerEvadeCheckStrategy implements MoveStrategy {

  public getLegalMoves(_position: Position | null, boardState: BoardState): Position[] {
    const legalMoves: Position[] = [];

    // Check if the computer is in check
    const checkmateChecker = new CheckmateChecker(boardState);
    const checkInfo = checkmateChecker.isInCheck('Computer');
    if (checkInfo.inCheck) {
      // Filter legal moves to only those that get the king out of check
      for (const piece of boardState.pieces) {
        if (piece.color === 'white') {
          const pieceLegalMoves = piece.moveStrategy.getLegalMoves(piece.position, boardState);
          // Make sure pieceLegalMoves is an iterable array before using it
          if (pieceLegalMoves && Array.isArray(pieceLegalMoves)) {
            for (const move of pieceLegalMoves) {
              const simulatedBoardState = this.simulateMove(piece.position, move, boardState);
              if (!checkmateChecker.isInCheckAfterMove('Computer', simulatedBoardState)) {
                move.piece = piece;
                legalMoves.push(move);
              }
            }
          }
        }
      }
    } else {
      // If not in check, return all legal moves
      for (const piece of boardState.pieces) {
        if (piece.color === 'white') {
          const pieceLegalMoves = piece.moveStrategy.getLegalMoves(piece.position, boardState);
          pieceLegalMoves.forEach(move => move.piece = piece);
          legalMoves.push(...pieceLegalMoves);
        }
      }
    }

    // If we have legal moves, prioritize captures when possible
    if (legalMoves.length > 0) {
      const captureMoves = legalMoves.filter(move => 
        CaptureMoveDetector.isCaptureMove(move, boardState, 'white')
      );
      
      if (captureMoves.length > 0) {
        // Sort captures by value
        const prioritizedCaptures = CaptureMoveDetector.prioritizeCaptureMoves(captureMoves, boardState);
        const bestCapture = prioritizedCaptures[0];
        
        const capturedPiece = boardState.pieces.find(p => 
          p.position.row === bestCapture.row && 
          p.position.col === bestCapture.col && 
          p.color !== 'white'
        );
        
        logger.debug(`EvadeCheckStrategy: Prioritizing capture move - Piece ${bestCapture.piece!.type} capturing ${capturedPiece?.type || 'piece'} at (${bestCapture.row}, ${bestCapture.col})`);
        logger.debug(`Computer (EvadeCheckStrategy) will capture with ${bestCapture.piece!.type} at position (${bestCapture.row}, ${bestCapture.col})`);
        return [bestCapture];
      }
    }
    
    return legalMoves;
  }

  getStrategyName(): string {
    return "Computer Evade Check Strategy";
  }

  private simulateMove(from: Position, to: Position, boardState: BoardState): BoardState {
    const simulatedBoardState = new BoardState(
      boardState.pieces.map(piece => ({
        ...piece,
        moveStrategy: Object.create(Object.getPrototypeOf(piece.moveStrategy), Object.getOwnPropertyDescriptors(piece.moveStrategy))
      }))
    );

    const piece = simulatedBoardState.pieces.find(p => p.position.row === from.row && p.position.col === from.col);
    if (piece) {
      piece.position = to;
    }

    return simulatedBoardState;
  }
}
